// NOTE: server-only imports are loaded inside each .handler() body so the
// service-role client cannot leak into the client bundle via this
// client-reachable `.functions.ts` module.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";



export type HealthScreeningRow = {
  id: string;
  user_id: string;
  screening_type: string;
  last_done_date: string | null;
  next_due_date: string | null;
  notes: string | null;
  provider: string | null;
  created_at: string;
  updated_at: string;
};

const SCREENING_TYPES = [
  "mammogram",
  "dexa",
  "lipid_panel",
  "a1c",
  "colonoscopy",
  "pap_hpv",
  "blood_pressure",
  "eye_exam",
] as const;

type ScreeningType = (typeof SCREENING_TYPES)[number];

// Default re-screening intervals, expressed in months.
const DEFAULT_INTERVAL_MONTHS: Record<ScreeningType, number> = {
  mammogram: 12,
  dexa: 24,
  lipid_panel: 24,
  a1c: 12,
  colonoscopy: 120,
  pap_hpv: 60,
  blood_pressure: 6,
  eye_exam: 24,
};

function addMonthsISO(dateISO: string, months: number): string {
  // dateISO is YYYY-MM-DD. Treat as UTC to avoid timezone drift.
  const [y, m, d] = dateISO.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCMonth(dt.getUTCMonth() + months);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

const accessTokenSchema = z.object({ accessToken: z.string().min(10) });
const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "date must be YYYY-MM-DD");

// ---------- LIST ------------------------------------------------------

export const listHealthScreenings = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => accessTokenSchema.parse(data))
  .handler(async ({ data }): Promise<HealthScreeningRow[]> => {
    const { authFromAccessToken } = await import("@/server/labs/auth.server");
    const { assertActiveAccess } = await import("@/integrations/supabase/access-check.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { safeError } = await import("./safe-error.server");
    const { userId } = await authFromAccessToken(data.accessToken);
    await assertActiveAccess(userId);
    const { data: rows, error } = await supabaseAdmin
      .from("health_screenings")
      .select("*")
      .eq("user_id", userId)
      .order("screening_type", { ascending: true });
    if (error) throw safeError(error, { scope: "screenings.list", userMessage: "Couldn't load your screenings." });
    return (rows || []) as HealthScreeningRow[];
  });

// ---------- SAVE (upsert) --------------------------------------------

const saveSchema = z.object({
  screening_type: z.enum(SCREENING_TYPES),
  last_done_date: dateSchema.nullable().optional(),
  next_due_date: dateSchema.nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
  provider: z.string().max(255).nullable().optional(),
});

export const saveHealthScreening = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    saveSchema.merge(accessTokenSchema).parse(data),
  )
  .handler(async ({ data }): Promise<HealthScreeningRow> => {
    const { authFromAccessToken } = await import("@/server/labs/auth.server");
    const { assertActiveAccess } = await import("@/integrations/supabase/access-check.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { safeError } = await import("./safe-error.server");
    const { userId } = await authFromAccessToken(data.accessToken);
    await assertActiveAccess(userId);

    let nextDue = data.next_due_date ?? null;
    if (!nextDue && data.last_done_date) {
      const months = DEFAULT_INTERVAL_MONTHS[data.screening_type];
      nextDue = addMonthsISO(data.last_done_date, months);
    }

    const row = {
      user_id: userId,
      screening_type: data.screening_type,
      last_done_date: data.last_done_date ?? null,
      next_due_date: nextDue,
      notes: data.notes ?? null,
      provider: data.provider ?? null,
      updated_at: new Date().toISOString(),
    };

    const { data: saved, error } = await supabaseAdmin
      .from("health_screenings")
      .upsert(row, { onConflict: "user_id,screening_type" })
      .select("*")
      .single();
    if (error) throw safeError(error, { scope: "screenings.save", userMessage: "Couldn't save that screening." });
    return saved as HealthScreeningRow;
  });

// ---------- DELETE ----------------------------------------------------

export const deleteHealthScreening = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    accessTokenSchema.extend({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data }) => {
    const { authFromAccessToken } = await import("@/server/labs/auth.server");
    const { assertActiveAccess } = await import("@/integrations/supabase/access-check.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { safeError, notFoundError } = await import("./safe-error.server");
    const { userId } = await authFromAccessToken(data.accessToken);
    await assertActiveAccess(userId);
    const { data: row, error: rErr } = await supabaseAdmin
      .from("health_screenings")
      .select("id, user_id")
      .eq("id", data.id)
      .maybeSingle();
    if (rErr) throw safeError(rErr, { scope: "screenings.delete", userMessage: "Couldn't delete that screening." });
    if (!row || row.user_id !== userId) throw notFoundError("screenings.delete");

    const { error: delErr } = await supabaseAdmin
      .from("health_screenings")
      .delete()
      .eq("id", data.id);
    if (delErr) throw safeError(delErr, { scope: "screenings.delete", userMessage: "Couldn't delete that screening." });
    return { ok: true };
  });

